import { useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { message } from "antd"
import { Message, UseInterviewCleanupProps } from "../components/types"



export const useInterviewCleanup = ({
    stream,
    setStream,
    setIsCameraOn,
    setIsMicOn,
    setIsRecording,
    videoRef,
    stopTTS,
    disconnect,
    stopRecording,
    waitForRecordingFinalize,
    getFinalRecordingUrl,
    stopRecognition,
    displayStreamRef,
    micStreamRef,
    audioTrackRef,
    audioContextRef,
    destinationRef,
    micSourceNodeRef,
    micGainNodeRef,
    timerRef,
}: UseInterviewCleanupProps) => {
    const navigate = useNavigate()
    const wasTerminatedByScreenShareRef = useRef<boolean>(false)

    const stopStreamTracks = useCallback((mediaStream?: MediaStream | null) => {
        if (!mediaStream) return
        mediaStream.getTracks().forEach((track) => {
            try {
                track.stop()
            } catch (error) {
                console.error("Error stopping track:", error)
            }
        })
    }, [])

    const performEndInterview = useCallback(async (elapsedTime: number, messages: Message[]) => {
        let finalRecordingUrl: string | null = null
        let endedSuccessfully = !wasTerminatedByScreenShareRef.current

        try {
            // Stop Myra's voice immediately
            try {
                stopTTS()
            } catch (error) {
                console.error("Error stopping TTS:", error)
            }

            // Stop all audio elements
            try {
                if ("speechSynthesis" in window) {
                    speechSynthesis.cancel()
                    speechSynthesis.cancel()
                }
            } catch (error) {
                console.error("Error canceling speech synthesis:", error)
            }

            try {
                const audioElements = document.querySelectorAll("audio")
                audioElements.forEach((audio) => {
                    audio.pause()
                    audio.currentTime = 0
                    audio.src = ""
                })
            } catch (error) {
                console.error("Error stopping audio elements:", error)
            }

            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }

            // Stop speech recognition
            stopRecognition()

            // Stop recording
            await stopRecording()
            await waitForRecordingFinalize()

            // Stop screen share stream
            try {
                stopStreamTracks(displayStreamRef.current)
                displayStreamRef.current = null

                if ((window as any).__cqDisplayStream) {
                    stopStreamTracks((window as any).__cqDisplayStream as MediaStream)
                    delete (window as any).__cqDisplayStream
                }
            } catch (error) {
                console.error("Error stopping screen share:", error)
            }

            // Disconnect and stop mic gain and source nodes
            try {
                if (micGainNodeRef.current) {
                    micGainNodeRef.current.disconnect()
                    micGainNodeRef.current = null
                }
                if (micSourceNodeRef.current) {
                    micSourceNodeRef.current.disconnect()
                    micSourceNodeRef.current = null
                }
            } catch (error) {
                console.error("Error disconnecting mic nodes:", error)
            }

            // Stop microphone stream
            try {
                stopStreamTracks(micStreamRef.current)
                micStreamRef.current = null
            } catch (error) {
                console.error("Error stopping mic stream:", error)
            }

            // Stop audio track ref
            try {
                if (audioTrackRef.current) {
                    audioTrackRef.current.stop()
                    audioTrackRef.current.enabled = false
                    audioTrackRef.current = null
                }
            } catch (error) {
                console.error("Error stopping audio track:", error)
            }

            // Stop user camera/mic stream
            try {
                stopStreamTracks(stream)
                setStream(null)
            } catch (error) {
                console.error("Error stopping user stream:", error)
            }

            // Clear video element
            try {
                if (videoRef.current) {
                    (videoRef.current as HTMLVideoElement).srcObject = null
                }
            } catch (error) {
                console.error("Error clearing video element:", error)
            }

            // Close audio context
            try {
                if (audioContextRef.current) {
                    await audioContextRef.current.close()
                    audioContextRef.current = null
                }
                destinationRef.current = null
            } catch (error) {
                console.error("Error closing audio context:", error)
            }

            // Disconnect socket
            try {
                disconnect()
            } catch (error) {
                console.error("Error disconnecting socket:", error)
            }

            finalRecordingUrl = getFinalRecordingUrl()

            // Show appropriate message
            if (wasTerminatedByScreenShareRef.current) {
                message.warning("Interview terminated - Screen sharing was stopped")
            } else {
                message.success("Interview ended successfully")
            }
        } catch (error) {
            endedSuccessfully = false
            console.error("Error ending call:", error)
            message.error("Error ending interview")
        } finally {
            setIsCameraOn(false)
            setIsMicOn(false)
            setIsRecording(false)

            let invitationIdForSummary: string | undefined
            try {
                invitationIdForSummary = localStorage.getItem("invitationId") || undefined
            } catch (error) {
                console.error("Error reading invitationId from storage:", error)
                invitationIdForSummary = undefined
            }

            // Clear invitation ID
            try {
                localStorage.removeItem("invitationId")
            } catch (error) {
                console.error("Error removing invitationId from storage:", error)
            }

            const summaryState = {
                elapsedTime,
                messagesCount: messages.length,
                recordingUrl: finalRecordingUrl,
                endedSuccessfully,
                invitationId: invitationIdForSummary,
            }

            navigate("/interview-completed", { state: summaryState })
        }
    }, [
        stopTTS,
        stopRecognition,
        stopRecording,
        waitForRecordingFinalize,
        stopStreamTracks,
        displayStreamRef,
        micStreamRef,
        audioTrackRef,
        stream,
        setStream,
        videoRef,
        audioContextRef,
        destinationRef,
        micGainNodeRef,
        micSourceNodeRef,
        disconnect,
        getFinalRecordingUrl,
        setIsCameraOn,
        setIsMicOn,
        setIsRecording,
        navigate,
        timerRef,
    ])

    return {
        performEndInterview,
        wasTerminatedByScreenShareRef,
    }
}

